/**
 * Getting a wallet, for someone who does not have one yet.
 *
 * ZERO IMPORTS, per invariant 23.
 *
 * The console used to answer "No Solana wallet detected" and stop. That single
 * grey sentence is the end of the road for every visitor who has just
 * registered, confirmed their address, signed in — and has no wallet. It names
 * the problem and offers nothing, which is the same failure as a metadata URI
 * that 404s with a 200 (invariant 22): the message is technically true and
 * practically useless.
 *
 * It also conflated two genuinely different situations. A browser with NOTHING
 * installed and a browser with a wallet that registered but cannot sign Solana
 * transactions both printed the same words, and the fix for each is opposite:
 * install something, versus unlock or enable the thing you already have.
 */

export interface WalletSuggestion {
  name: string;
  /** Verified against the vendor's own site. */
  url: string;
  note: string;
  /** True when it ships with the browser and needs no install. */
  builtIn?: boolean;
}

/**
 * Deliberately short, and every URL was checked against the vendor's own site
 * rather than recalled. A stale link on this panel sends a person hunting for a
 * crypto wallet through a search engine, which is precisely how people end up
 * installing a malicious clone.
 */
export const WALLET_SUGGESTIONS: WalletSuggestion[] = [
  {
    name: "Phantom",
    url: "https://phantom.com/download",
    note: "Browser extension. The most widely used Solana wallet.",
  },
  {
    name: "Solflare",
    url: "https://solflare.com",
    note: "Browser extension, Solana only.",
  },
  {
    name: "Brave Wallet",
    url: "https://brave.com/wallet/",
    note: "Built into the Brave browser — nothing to install. Enable it in Settings, then reload this page.",
    builtIn: true,
  },
];

export type WalletVerdictKind = "ready" | "none" | "unusable";

export interface WalletVerdict {
  kind: WalletVerdictKind;
  headline: string;
  detail: string;
}

export interface WalletCensus {
  /** Wallets that registered themselves with the page, whatever they can do. */
  registered: number;
  /** Of those, the ones that can actually connect and sign for Solana. */
  usable: number;
  /** Names of the registered-but-unusable ones, for the message. */
  rejected: string[];
}

/**
 * Which of the three situations this browser is in.
 *
 * The distinction between "none" and "unusable" is the whole point: a wallet
 * that registered but was filtered out is already installed, and telling that
 * person to install a wallet sends them in a circle.
 */
export function walletVerdict(c: WalletCensus): WalletVerdict {
  if (c.usable > 0) {
    return { kind: "ready", headline: "Wallet detected", detail: "" };
  }
  if (c.registered > 0) {
    const names = c.rejected.length ? c.rejected.join(", ") : "A wallet";
    return {
      kind: "unusable",
      headline: `${names} is installed, but cannot sign Solana transactions here`,
      detail:
        "It may be locked, or set to a different network, or it may not support Solana at all. " +
        "Unlock it, switch it to Solana, and reload this page. If it still does not appear, " +
        "one of the wallets below will work.",
    };
  }
  return {
    kind: "none",
    headline: "No Solana wallet yet",
    detail:
      "A wallet is what holds your keys and signs transactions. oxMinter never sees them: " +
      "it asks your wallet to sign, and your wallet decides. Install one of these, then reload this page.",
  };
}

/**
 * The safety line that belongs beside any list of wallet downloads.
 *
 * A crypto wallet is the single most cloned kind of software there is, and a
 * page that hands someone a list of wallets to install owes them this sentence.
 */
export const WALLET_SAFETY =
  "Only install a wallet from its official site, and check the address in your browser bar. " +
  "No wallet, and no part of oxMinter, will ever ask you to type a seed phrase into a web page.";
